import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export { default as BackButton } from './BackButton.vue';
export { default as Button } from './Button.vue';
export { default as CopyButton } from './CopyButton.vue';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-medium h-fit overflow-hidden ' +
    'text-sm transition-all disabled:pointer-events-none disabled:opacity-50 [&_i]:text-xs ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 " +
    'outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px] ' +
    'data-[loading=loading]:[&>[class*="loading-spinner"]]:text-background ' +
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        icon: 'size-9',
        'icon-lg': 'size-10',
        'icon-sm': 'size-8',
        'icon-xs': 'size-7 text-xs',
        inline:
          'h-auto p-0 m-0 text-[length:inherit] rounded-none ' +
          '[&>[class*="fa-"]]:not([class*="fa-stack"]):text-[length:inherit]',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        sm: 'h-8 text-xs px-3 has-[>svg]:px-2.5',
        xs: 'h-7 text-2xs px-2.5 has-[>svg]:px-1.5 gap-1.5',
      },
      variant: {
        agent: 'bg-agent hover:bg-fade-agent text-agent-foreground',
        danger: 'bg-danger hover:bg-fade-danger text-danger-foreground',
        default:
          'bg-primary text-primary-foreground hover:bg-fade-primary data-[loading=loading]:[&>[class*="loading-spinner"]]:text-primary-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-fade-destructive focus-visible:ring-destructive/20 ' +
          'dark:focus-visible:ring-destructive/40',
        ghost:
          'hover:bg-accent dark:hover:bg-accent/50 data-[loading=loading]:[&>[class*="loading-spinner"]]:text-foreground',
        link: 'link-btn text-primary underline-offset-4 hover:underline min-h-5 cursor-pointer',
        'link-dotted':
          'underline decoration-dashed decoration-muted-foreground decoration-1 underline-offset-4 cursor-pointer hover:text-primary',
        outline:
          'border bg-background hover:bg-accent data-[loading=loading]:[&>[class*="loading-spinner"]]:text-foreground ' +
          'dark:border-input dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        primary:
          'bg-primary text-primary-foreground hover:bg-fade-primary data-[loading=loading]:[&>[class*="loading-spinner"]]:text-primary-foreground',
        onyx: 'bg-black dark:bg-gray-800 text-white hover:bg-black dark:hover:bg-gray-950 data-[loading=loading]:[&>[class*="loading-spinner"]]:text-white',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/70 data-[loading=loading]:[&>[class*="loading-spinner"]]:text-foreground',
        success: 'bg-success hover:bg-fade-success text-success-foreground',
        tertiary: 'bg-tertiary hover:bg-fade-tertiary text-tertiary-foreground',
        transparent:
          'bg-transparent text-foreground hover:text-foreground/60 data-[loading=loading]:[&>[class*="loading-spinner"]]:text-foreground',
        warning: 'bg-warning hover:bg-fade-warning text-warning-foreground',
      },
    },
  },
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;
